import { Stack, RadioGroup, FormControlLabel, Radio, TextField, Button, Typography, CircularProgress } from '@mui/material'
import format from 'date-fns/format'
import startOfDay from 'date-fns/startOfDay'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toUserTimezoneDateSelectorFormat } from './utils'

export type EditAppointmentFormData = {
  action: string
  rescheduledDate: string
  rescheduledTime: string
}

type EditAppointmentFormProps = {
  onSubmit: (formData: EditAppointmentFormData) => void
  allowSimpleEditing?: boolean
  formData: EditAppointmentFormData
  setFormData: (formData: EditAppointmentFormData) => void
  isSubmitting: boolean
}

export function EditAppointmentForm({ onSubmit, allowSimpleEditing, formData, setFormData, isSubmitting }: EditAppointmentFormProps) {
  const { t } = useTranslation()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit(formData)
  }

  const minDate = useMemo(() => toUserTimezoneDateSelectorFormat(new Date()), [])
  const minTime = useMemo(() => {
    if (!formData.rescheduledDate) return undefined

    const today = startOfDay(new Date())
    const selectedDate = startOfDay(new Date(formData.rescheduledDate))

    if (selectedDate.getTime() === today.getTime()) {
      return format(new Date(), 'HH:mm')
    }

    return undefined
  }, [formData.rescheduledDate])

  const enableSave = useMemo(() => {
    if (allowSimpleEditing) {
      return formData.action === 'cancel' || (!!formData.rescheduledDate && !!formData.rescheduledTime)
    }

    return !!formData.action
  }, [allowSimpleEditing, formData.action, formData.rescheduledDate, formData.rescheduledTime])

  return (
    <>
      <Typography variant="h2">{t('common.label.editAppointment')}</Typography>
      <Stack component="form" py={2} onSubmit={handleSubmit}>
        <RadioGroup
          aria-label="edit appointment action"
          name="editAppointmentAction"
          value={formData.action}
          onChange={(event) => setFormData({ rescheduledDate: '', rescheduledTime: '', action: event.target.value })}
        >
          <FormControlLabel value="cancel" control={<Radio />} label={t('common.cancel')} />
          <FormControlLabel value="reschedule" control={<Radio />} label={t('common.label.reschedule')} />
          {formData.action === 'reschedule' && allowSimpleEditing && (
            <Stack direction="row" spacing={2} sx={{ my: 2 }}>
              <TextField
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate }}
                label={t('common.label.date')}
                value={formData.rescheduledDate}
                onChange={(e) => setFormData({ ...formData, rescheduledDate: e.target.value })}
                sx={{ flex: 3 }}
              />
              <TextField
                size="small"
                type="time"
                inputProps={{ min: minTime }}
                InputLabelProps={{ shrink: true }}
                label={t('common.label.time')}
                value={formData.rescheduledTime}
                onChange={(e) => setFormData({ ...formData, rescheduledTime: e.target.value })}
                sx={{ flex: 2 }}
              />
            </Stack>
          )}
        </RadioGroup>
        <Button
          variant="contained"
          type="submit"
          sx={{ marginLeft: 'auto' }}
          disabled={!enableSave || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {t(allowSimpleEditing ? 'common.save' : 'common.next')}
        </Button>
      </Stack>
    </>
  )
}
